import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ArrowLeft } from "lucide-react";

const T = {
  tr: {
    title: "Şifremi unuttum",
    subtitle: "Kayıtlı e-posta adresini gir. Sana bir sıfırlama bağlantısı göndereceğiz.",
    email: "E-posta",
    submit: "Sıfırlama bağlantısı gönder",
    submitting: "Gönderiliyor…",
    backLogin: "Girişe geri dön",
    safeMsg: "Eğer bu e-posta kayıtlıysa, şifre sıfırlama bağlantısı gönderildi.",
    checkInbox: "E-postanı ve spam klasörünü kontrol et. Bağlantı 30 dakika geçerlidir.",
  },
  de: {
    title: "Passwort vergessen",
    subtitle: "Gib deine registrierte E-Mail ein. Wir senden dir einen Link zum Zurücksetzen.",
    email: "E-Mail",
    submit: "Zurücksetzen-Link senden",
    submitting: "Wird gesendet…",
    backLogin: "Zurück zur Anmeldung",
    safeMsg: "Wenn diese E-Mail registriert ist, wurde ein Link zum Zurücksetzen gesendet.",
    checkInbox: "Prüfe Postfach und Spam-Ordner. Der Link ist 30 Minuten gültig.",
  },
};

export default function ForgotPassword() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase(), language: lang });
      setSent(true);
    } catch (err) {
      // Never leak — endpoint always returns 200, but be defensive.
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg">R</div>
          <span className="font-display font-bold text-lg">RestroPilot</span>
        </div>

        <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4 mb-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
            <KeyRound className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-sm tracking-tight">{t.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t.subtitle}</div>
          </div>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div>
              <Label>{t.email}</Label>
              <Input
                type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@restaurant.com"
                data-testid="forgot-email"
                className="mt-1"
              />
            </div>
            <Button type="submit" disabled={loading || !email} className="w-full h-11 active:scale-95" data-testid="forgot-submit">
              {loading ? t.submitting : t.submit}
            </Button>
          </form>
        ) : (
          <div className="rounded-2xl border p-6 bg-emerald-50 border-emerald-200 space-y-2" data-testid="forgot-sent">
            <div className="font-medium text-emerald-900">{t.safeMsg}</div>
            <div className="text-xs text-emerald-800">{t.checkInbox}</div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="forgot-back-login">
            <ArrowLeft className="h-3.5 w-3.5" /> {t.backLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
